import type { Intent } from "../../tokens";

/**
 * Metric — the one cohesive metric-surface data shape (docs/design/METRIC-PATTERN-PRD.md).
 *
 * A metric is a READING judged against a REFERENCE → a VERDICT, optionally over
 * HISTORY, with an INSIGHT and a DRILL. The pulse band, the KPI hero curve, the
 * objective trajectory, and the stat tile are the same thing at different
 * densities — they read this one shape and render through one `MetricChart`.
 */

// ── two verdict scales, kept type-distinct (a discriminated union, not a flat enum) ──
/** healthy-NOW (the pulse / objectives band). */
export type BandVerdict = "ok" | "watch" | "risk" | "pending";
/** on-PACE by the date (objectives). */
export type PaceVerdict = "ahead" | "on-pace" | "behind" | "at-risk" | "pending";

/**
 * The banded judgment, discriminated by `scale` so a pulse metric can never
 * carry a pace verdict and vice-versa. The CALLER supplies the rendered
 * `{ label, intent }` — the block never re-interprets the enum, so the two
 * vocabularies stay where they live (the shape HeroKpi + objectives already use).
 */
export type MetricVerdict =
  | { scale: "band"; value: BandVerdict; label: string; intent: Intent }
  | { scale: "pace"; value: PaceVerdict; label: string; intent: Intent };

/**
 * The benchmark the reading is judged against — the SINGLE prop that
 * distinguishes the four charts. May be an array so an objective draws both a
 * flat goal line AND a pace ramp at once.
 */
export type MetricReference =
  | { mode: "none" }
  | { mode: "flat-target"; value: number; label?: string }
  | { mode: "pace-ramp"; start: number; target: number; elapsed: number; label?: string }
  /** A per-point reference polyline — covers KpiCurve's cumulative `target.pace`. */
  | { mode: "series"; points: number[]; label?: string };

export interface Metric {
  key: string;
  label: string;
  subtitle?: string;
  /** PRE-FORMATTED via @excellent/ui formatters — the only string ever rendered. */
  reading: string | null;
  /** Chart-math only; never rendered. */
  value: number | null;
  reference: MetricReference | MetricReference[];
  verdict: MetricVerdict;
  /** An objective renders BOTH (e.g. healthy now AND behind pace). */
  secondaryVerdict?: MetricVerdict;
  history?: number[];
  delta?: { display: string; tone: "up" | "down" | "none" };
  insight?: { text: string; lead?: boolean };
  href?: string;
}

// ── verdict → { label, intent } builders (the vocabulary the caller owns) ──────
// The numeric classification (band(health, target)) lives in pulse-wall-shared
// — these only turn a chosen enum value into its rendered label + intent.
export const BAND_VERDICT_META: Record<BandVerdict, { label: string; intent: Intent }> = {
  ok: { label: "Healthy", intent: "success" },
  watch: { label: "Watch", intent: "warning" },
  risk: { label: "At risk", intent: "danger" },
  pending: { label: "No signal", intent: "neutral" },
};
export const PACE_VERDICT_META: Record<PaceVerdict, { label: string; intent: Intent }> = {
  ahead: { label: "Ahead of pace", intent: "success" },
  "on-pace": { label: "On pace", intent: "success" },
  behind: { label: "Behind pace", intent: "warning" },
  "at-risk": { label: "At risk", intent: "danger" },
  pending: { label: "Pending — no signal", intent: "neutral" },
};

export const bandVerdict = (value: BandVerdict): MetricVerdict => ({
  scale: "band",
  value,
  ...BAND_VERDICT_META[value],
});
export const paceVerdict = (value: PaceVerdict): MetricVerdict => ({
  scale: "pace",
  value,
  ...PACE_VERDICT_META[value],
});

/** Intent → the CSS color used to tint a chart's end-dot. */
export const INTENT_CHART_COLOR: Record<Intent, string> = {
  success: "var(--success)",
  warning: "var(--warning)",
  danger: "var(--danger)",
  info: "var(--primary)",
  neutral: "var(--chart-1)",
};

/** The flat-target/pace-ramp label, for the reading row ("target $70k"). */
export function referenceLabel(reference: MetricReference | MetricReference[]): string | null {
  const refs = Array.isArray(reference) ? reference : [reference];
  for (const r of refs) {
    if (r.mode === "flat-target") return r.label ?? `target ${r.value}`;
    if (r.mode === "pace-ramp") return r.label ?? `target ${r.target}`;
    if (r.mode === "series") return r.label ?? null;
  }
  return null;
}
